import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Container,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
  Chip,
  Pagination,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  Badge,
  alpha,
} from "@mui/material";
import {
  Search,
  Close,
  ArrowBack,
  ArrowForward,
  Download,
  Share,
  Image as ImageIcon,
  VideoLibrary,
  Collections,
  CalendarToday,
  PlayCircle,
  Photo,
  Movie,
  DateRange,
  Tag,
  Sort,
  ViewModule,
  ViewList,
  FilterAlt,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { useAuth } from "../../auth/AuthContext";

// Utility function to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : url;
};

const GalleryPage = () => {
  const [allMedia, setAllMedia] = useState([]);
  const [filteredMedia, setFilteredMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [mediaType, setMediaType] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid");
  const [activeYear, setActiveYear] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const imagesPerPage = 12;
  const dialogRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const { getData } = useAuth();

  // Extract years and categories from media
  const years = ["all", ...new Set(allMedia
    .map(item => new Date(item.dateCreated).getFullYear())
    .filter(year => !isNaN(year))
    .sort((a, b) => b - a))];

  const categories = ["all", "STEAM", "Robotics", "Coding", "Competition", "Event", "Students", "Workshop", "General"];

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      mirror: true,
      offset: 100,
    });
  }, []);

  // Fetch gallery data from API
  useEffect(() => {
    const fetchGalleryData = async () => {
      setLoading(true);
      try {
        const responseGallery = await getData("proccess.php", "gallery");
        
        // Transform and categorize data
        const mediaItems = responseGallery.data.map(item => {
          const type = item.video ? "video" : "image";
          const imageUrl = item.image 
            ? `https://mindsinaction.com.na/assets/media/pictures/${item.image}`
            : null;
          const videoId = item.video ? getYouTubeVideoId(item.video) : null;
          const date = new Date(item.date);
          const tags = extractTagsFromName(item.image_name);
          const category = determineCategory(item.category, tags);
          
          return {
            id: item.id,
            title: item.image_name || "Untitled",
            description: item.image_name || "",
            type: type,
            image: imageUrl,
            video: item.video,
            videoId: videoId,
            dateCreated: item.date,
            date: date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            year: date.getFullYear(),
            tags: tags,
            category: category,
            thumbnail: type === "video" 
              ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
              : imageUrl,
          };
        });

        setAllMedia(mediaItems);
        setFilteredMedia(mediaItems);
      } catch (error) {
        console.error("Error fetching gallery data:", error);
        // Fallback to sample data
        const sampleMedia = generateSampleMedia();
        setAllMedia(sampleMedia);
        setFilteredMedia(sampleMedia);
      } finally {
        setLoading(false);
        setTimeout(() => AOS.refresh(), 100);
      }
    };

    fetchGalleryData();
  }, [getData]);

  // Helper functions remain the same
  const determineCategory = (imageName, tags) => {
    if (!imageName) return "General";
    const name = imageName.toLowerCase();
    
    if (name.includes("steam") || tags.includes("STEAM")) return "STEAM";
    if (name.includes("robotics") || tags.includes("Robotics")) return "Robotics";
    if (name.includes("coding") || tags.includes("Coding")) return "Coding";
    if (name.includes("competition") || tags.includes("Competition")) return "Competition";
    if (name.includes("workshop") || name.includes("training")) return "Workshop";
    if (name.includes("event") || tags.includes("Event")) return "Event";
    if (name.includes("student") || tags.includes("Students")) return "Students";
    
    return "General";
  };

  const extractTagsFromName = (imageName) => {
    if (!imageName) return ["Gallery"];
    
    const name = imageName.toLowerCase();
    const tags = ["Gallery"];
    
    if (name.includes("stem") || name.includes("science") || name.includes("technology") || name.includes("engineering") || name.includes("math")) {
      tags.push("STEAM");
    }
    if (name.includes("robotics") || name.includes("robot")) {
      tags.push("Robotics");
    }
    if (name.includes("coding") || name.includes("programming") || name.includes("software")) {
      tags.push("Coding");
    }
    if (name.includes("competition") || name.includes("contest") || name.includes("challenge")) {
      tags.push("Competition");
    }
    if (name.includes("event") || name.includes("program") || name.includes("activity")) {
      tags.push("Event");
    }
    if (name.includes("student") || name.includes("learner") || name.includes("youth")) {
      tags.push("Students");
    }
    if (name.includes("workshop") || name.includes("training") || name.includes("bootcamp")) {
      tags.push("Workshop");
    }
    
    return tags;
  };

  // Apply filters and search
  useEffect(() => {
    let result = [...allMedia];

    if (mediaType !== "all") {
      result = result.filter(item => item.type === mediaType);
    }

    if (activeYear !== "all") {
      result = result.filter(item => item.year === parseInt(activeYear));
    }

    if (activeCategory !== "all") {
      result = result.filter(item => item.category === activeCategory || item.tags.includes(activeCategory));
    }

    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          (item.title && item.title.toLowerCase().includes(term)) ||
          (item.description && item.description.toLowerCase().includes(term)) ||
          (item.tags && item.tags.some((tag) => tag.toLowerCase().includes(term))) ||
          (item.category && item.category.toLowerCase().includes(term))
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.dateCreated) - new Date(a.dateCreated);
        case "oldest":
          return new Date(a.dateCreated) - new Date(b.dateCreated);
        case "title":
          return (a.title || "").localeCompare(b.title || "");
        default:
          return 0;
      }
    });

    setFilteredMedia(result);
    setPage(1);
  }, [allMedia, searchTerm, mediaType, sortBy, activeYear, activeCategory]);

  // Get current page media
  const indexOfLastMedia = page * imagesPerPage;
  const indexOfFirstMedia = indexOfLastMedia - imagesPerPage;
  const currentMedia = filteredMedia.slice(indexOfFirstMedia, indexOfLastMedia);
  const totalPages = Math.ceil(filteredMedia.length / imagesPerPage);

  // Handle media click for lightbox/dialog
  const handleMediaClick = (media) => {
    setSelectedMedia(media);
    document.body.style.overflow = "hidden";
  };

  const handleCloseLightbox = () => {
    setSelectedMedia(null);
    document.body.style.overflow = "auto";
  };

  // Navigation functions remain the same
  const handleNextMedia = () => {
    const currentIndex = filteredMedia.findIndex(
      (item) => item.id === selectedMedia.id
    );
    if (currentIndex < filteredMedia.length - 1) {
      setSelectedMedia(filteredMedia[currentIndex + 1]);
    }
  };

  const handlePrevMedia = () => {
    const currentIndex = filteredMedia.findIndex(
      (item) => item.id === selectedMedia.id
    );
    if (currentIndex > 0) {
      setSelectedMedia(filteredMedia[currentIndex - 1]);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedMedia) return;

      switch (e.key) {
        case "Escape":
          handleCloseLightbox();
          break;
        case "ArrowRight":
          handleNextMedia();
          break;
        case "ArrowLeft":
          handlePrevMedia();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMedia]);

  // Download image
  const handleDownload = (imageUrl, title) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = title || "gallery-image";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Share media
  const handleShare = async (media) => {
    const shareData = {
      title: media.title || "Gallery Media",
      text: media.description || "Check out this from Minds in Action Gallery",
    };

    if (media.type === "image") {
      shareData.url = media.image;
    } else if (media.type === "video") {
      shareData.url = `https://www.youtube.com/watch?v=${media.videoId}`;
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log("Sharing cancelled:", error);
      }
    } else {
      navigator.clipboard.writeText(shareData.url || window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  // Generate sample media
  const generateSampleMedia = () => {
    const categories = ["Steam", "Robotics", "Coding", "Competition", "Event", "Students", "Workshop"];
    const years = [2024, 2023, 2022];
    
    return Array.from({ length: 48 }, (_, i) => {
      const type = i % 2 === 0 ? "image" : "video";
      const category = categories[i % categories.length];
      const year = years[i % years.length];
      const month = String((i % 12) + 1).padStart(2, "0");
      const day = String((i % 28) + 1).padStart(2, "0");
      
      return {
        id: i + 1,
        title: `${category} Program ${i + 1}`,
        description: `This is a sample ${type} from our ${category.toLowerCase()} program showing students engaged in hands-on learning.`,
        type: type,
        image: type === "image" 
          ? `https://mindsinaction.com.na/assets/media/pictures/sample${(i % 8) + 1}.jpg`
          : null,
        video: type === "video" ? `https://www.youtube.com/watch?v=dQw4w9WgXcQ` : null,
        videoId: type === "video" ? "dQw4w9WgXcQ" : null,
        dateCreated: `${year}-${month}-${day}`,
        date: `${["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][i % 12]} ${day}, ${year}`,
        year: year,
        tags: [category, "Education", type === "image" ? "Photo" : "Video"],
        category: category,
        thumbnail: type === "video" 
          ? `https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg`
          : `https://mindsinaction.com.na/assets/media/pictures/sample${(i % 8) + 1}.jpg`,
      };
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        pt: '10vh',
        pb: 8,
      }}
    >
      {/* Hero Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #134e9c 0%, #90ca25 100%)",
          py: { xs: 4, md: 6 },
          mb: 4,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 20%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 20%)",
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ 
            textAlign: "center", 
            maxWidth: "800px", 
            mx: "auto",
            px: { xs: 2, sm: 0 }
          }}>
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontWeight: 900,
                fontSize: { xs: "2rem", sm: "3rem", md: "4rem" },
                mb: 2,
                textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
              }}
              data-aos="fade-down"
            >
              Gallery & Media
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255, 255, 255, 0.95)",
                mb: 4,
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
              }}
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Explore our visual journey through STEM education, robotics competitions, coding workshops, and inspiring student achievements
            </Typography>
          </Box>

          {/* Main Search Bar */}
          <Paper
            elevation={6}
            sx={{
              p: { xs: 2, sm: 3 },
              mx: "auto",
              maxWidth: "1000px",
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
            data-aos="fade-up"
            data-aos-delay="400"
          >
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              alignItems: { xs: "stretch", sm: "center" }
            }}>
              {/* Media Type Tabs */}
              <Box sx={{ 
                display: "flex", 
                gap: 1,
                flexShrink: 0,
                flexWrap: "wrap"
              }}>
                <Button
                  variant={mediaType === "all" ? "contained" : "outlined"}
                  onClick={() => setMediaType("all")}
                  startIcon={<Collections />}
                  sx={{
                    minWidth: "auto",
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: mediaType === "all" ? "#90ca25" : "transparent",
                    borderColor: mediaType === "all" ? "#90ca25" : "#ccc",
                    color: mediaType === "all" ? "white" : "#666",
                    "&:hover": {
                      backgroundColor: mediaType === "all" ? "#7cb342" : "rgba(144, 202, 37, 0.1)",
                      borderColor: "#90ca25",
                    }
                  }}
                >
                  All
                </Button>
                <Button
                  variant={mediaType === "image" ? "contained" : "outlined"}
                  onClick={() => setMediaType("image")}
                  startIcon={<Photo />}
                  sx={{
                    minWidth: "auto",
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: mediaType === "image" ? "#134e9c" : "transparent",
                    borderColor: mediaType === "image" ? "#134e9c" : "#ccc",
                    color: mediaType === "image" ? "white" : "#666",
                    "&:hover": {
                      backgroundColor: mediaType === "image" ? "#0d3c7c" : "rgba(19, 78, 156, 0.1)",
                      borderColor: "#134e9c",
                    }
                  }}
                >
                  Photos
                </Button>
                <Button
                  variant={mediaType === "video" ? "contained" : "outlined"}
                  onClick={() => setMediaType("video")}
                  startIcon={<Movie />}
                  sx={{
                    minWidth: "auto",
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: mediaType === "video" ? "#ff6b6b" : "transparent",
                    borderColor: mediaType === "video" ? "#ff6b6b" : "#ccc",
                    color: mediaType === "video" ? "white" : "#666",
                    "&:hover": {
                      backgroundColor: mediaType === "video" ? "#ff5252" : "rgba(255, 107, 107, 0.1)",
                      borderColor: "#ff6b6b",
                    }
                  }}
                >
                  Videos
                </Button>
              </Box>

              {/* Search Field */}
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search gallery..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "white",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm("")}
                          sx={{ mr: -1 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Filter Toggle Button */}
              <Box sx={{ 
                display: "flex", 
                gap: 1,
                flexShrink: 0
              }}>
                <Button
                  variant={showFilters ? "contained" : "outlined"}
                  onClick={() => setShowFilters(!showFilters)}
                  startIcon={<FilterAlt />}
                  sx={{
                    minWidth: "auto",
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: showFilters ? "#666" : "transparent",
                    borderColor: "#ccc",
                    color: showFilters ? "white" : "#666",
                    "&:hover": {
                      backgroundColor: showFilters ? "#555" : "rgba(0, 0, 0, 0.1)",
                    }
                  }}
                >
                  Filters
                </Button>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort By"
                    size="small"
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="oldest">Oldest First</MenuItem>
                    <MenuItem value="title">Title (A-Z)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Additional Filters */}
            {showFilters && (
              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", sm: "center" }
                }}
                data-aos="fade-up"
              >
                {/* Year Filter */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Filter by Year
                  </Typography>
                  <Box sx={{ 
                    display: "flex", 
                    gap: 1,
                    flexWrap: "wrap"
                  }}>
                    {years.map((year) => (
                      <Chip
                        key={year}
                        label={year === "all" ? "All Years" : year}
                        onClick={() => setActiveYear(year)}
                        color={activeYear === year ? "primary" : "default"}
                        variant={activeYear === year ? "filled" : "outlined"}
                        icon={activeYear === year ? <CheckCircle /> : <DateRange />}
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          "&:hover": {
                            backgroundColor: activeYear === year ? "primary.main" : "action.hover",
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Category Filter */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Filter by Category
                  </Typography>
                  <Box sx={{ 
                    display: "flex", 
                    gap: 1,
                    flexWrap: "wrap"
                  }}>
                    {categories.map((category) => (
                      <Chip
                        key={category}
                        label={category === "all" ? "All Categories" : category}
                        onClick={() => setActiveCategory(category)}
                        color={activeCategory === category ? "secondary" : "default"}
                        variant={activeCategory === category ? "filled" : "outlined"}
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          "&:hover": {
                            backgroundColor: activeCategory === category ? "secondary.main" : "action.hover",
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* View Mode */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    View Mode
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      onClick={() => setViewMode("grid")}
                      sx={{
                        backgroundColor: viewMode === "grid" ? "action.selected" : "transparent",
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        }
                      }}
                    >
                      <ViewModule />
                    </IconButton>
                    <IconButton
                      onClick={() => setViewMode("list")}
                      sx={{
                        backgroundColor: viewMode === "list" ? "action.selected" : "transparent",
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        }
                      }}
                    >
                      <ViewList />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      <Container maxWidth="xl">
        {/* Gallery Stats */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            mb: 3,
            gap: 2,
            p: 2,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#134e9c" }}>
              {filteredMedia.length} {mediaType === "all" ? "media items" : mediaType + "s"} found
            </Typography>
            {searchTerm && (
              <Typography variant="body2" color="text.secondary">
                Search results for: "{searchTerm}"
              </Typography>
            )}
          </Box>

          {/* Active Filters Display */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {mediaType !== "all" && (
              <Chip
                label={`Type: ${mediaType === "image" ? "Photos" : "Videos"}`}
                size="small"
                onDelete={() => setMediaType("all")}
                sx={{ backgroundColor: "#e3f2fd" }}
              />
            )}
            {activeYear !== "all" && (
              <Chip
                label={`Year: ${activeYear}`}
                size="small"
                onDelete={() => setActiveYear("all")}
                sx={{ backgroundColor: "#e8f5e9" }}
              />
            )}
            {activeCategory !== "all" && (
              <Chip
                label={`Category: ${activeCategory}`}
                size="small"
                onDelete={() => setActiveCategory("all")}
                sx={{ backgroundColor: "#fff3e0" }}
              />
            )}
          </Box>
        </Box>

        {/* Gallery Content */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : filteredMedia.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              backgroundColor: "white",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No media found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your search or filter criteria
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setSearchTerm("");
                setMediaType("all");
                setActiveYear("all");
                setActiveCategory("all");
              }}
              startIcon={<Close />}
            >
              Clear All Filters
            </Button>
          </Box>
        ) : (
          <>
            {/* Gallery Grid/List View */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: viewMode === "list" ? "center" : { xs: "center", sm: "flex-start" }
              }}
            >
              {currentMedia.map((media, index) => (
                <Box
                  key={media.id}
                  data-aos="zoom-in"
                  data-aos-delay={(index % 4) * 100}
                  sx={{
                    width: viewMode === "list" 
                      ? { xs: "100%", md: "100%" }
                      : { xs: "100%", sm: "calc(50% - 12px)", md: "calc(33.333% - 16px)", lg: "calc(25% - 18px)" },
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    backgroundColor: "white",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.2)",
                      "& .media-overlay": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                      "& .play-icon": {
                        transform: "scale(1.2)",
                      },
                    },
                  }}
                  onClick={() => handleMediaClick(media)}
                >
                  {/* Media Container */}
                  <Box
                    sx={{
                      position: "relative",
                      paddingTop: "66.67%", // 3:2 aspect ratio
                      backgroundColor: media.type === "video" ? "#000" : "#f5f5f5",
                      overflow: "hidden",
                    }}
                  >
                    {/* Media Content */}
                    {media.type === "image" ? (
                      <Box
                        component="img"
                        src={media.image || media.thumbnail}
                        alt={media.title}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.5s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                    ) : (
                      <>
                        <Box
                          component="img"
                          src={media.thumbnail}
                          alt={media.title}
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            opacity: 0.8,
                          }}
                        />
                        <Box
                          className="play-icon"
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            backgroundColor: "#3b76b2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            border: "3px solid white",
                            "&:hover": {
                              backgroundColor: "#90ca25",
                            },
                          }}
                        >
                          <PlayCircle sx={{ fontSize: 40, color: "white" }} />
                        </Box>
                      </>
                    )}

                    {/* Media Type Badge */}
                    <Chip
                      label={media.type === "image" ? "PHOTO" : "VIDEO"}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        backgroundColor: media.type === "image" ? "#134e9c" : "#90ca25",
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        height: 24,
                        "& .MuiChip-label": {
                          px: 1.5,
                        },
                      }}
                    />

                    {/* Hover Overlay */}
                    <Box
                      className="media-overlay"
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        color: "white",
                        padding: 2,
                        opacity: 0,
                        transform: "translateY(100%)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: "white",
                        }}
                      >
                        {media.title}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Media Info */}
                  <Box sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: "#134e9c",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {media.title}
                    </Typography>
                    
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "space-between",
                      mb: 1.5 
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 16, color: "#90ca25" }} />
                        <Typography variant="caption" color="text.secondary">
                          {media.date}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ 
                        color: "#134e9c", 
                        fontWeight: 600,
                        backgroundColor: "#e3f2fd",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1
                      }}>
                        {media.year}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Pagination */}
            {!loading && filteredMedia.length > 0 && totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 6,
                  pt: 4,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => {
                    setPage(value);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 2,
                      "&.Mui-selected": {
                        backgroundColor: "#90ca25",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#7cb342",
                        },
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Back to Home */}
        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              borderColor: "#90ca25",
              color: "#90ca25",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              "&:hover": {
                borderColor: "#7cb342",
                backgroundColor: "rgba(144, 202, 37, 0.04)",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(144, 202, 37, 0.2)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>

      {/* Lightbox/Dialog for Media */}
      <Dialog
        open={!!selectedMedia}
        onClose={handleCloseLightbox}
        maxWidth={selectedMedia?.type === "video" ? "md" : "lg"}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            maxHeight: "90vh",
            m: { xs: 1, sm: 2 },
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
        ref={dialogRef}
      >
        {selectedMedia && (
          <DialogContent sx={{ p: 0, position: "relative" }}>
            {/* Navigation and Action Buttons */}
            <Box sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 10,
              display: "flex",
              gap: 1,
            }}>
              <IconButton
                onClick={handleCloseLightbox}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>

            <Box sx={{
              position: "absolute",
              left: 16,
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              display: "flex",
              justifyContent: "space-between",
            }}>
              <IconButton
                onClick={handlePrevMedia}
                disabled={filteredMedia.findIndex((item) => item.id === selectedMedia.id) === 0}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                }}
              >
                <ArrowBack />
              </IconButton>

              <IconButton
                onClick={handleNextMedia}
                disabled={filteredMedia.findIndex((item) => item.id === selectedMedia.id) === filteredMedia.length - 1}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                }}
              >
                <ArrowForward />
              </IconButton>
            </Box>

            {/* Media Content */}
            <Box sx={{
              backgroundColor: "black",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}>
              {selectedMedia.type === "image" ? (
                <Box
                  component="img"
                  src={selectedMedia.image}
                  alt={selectedMedia.title}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Box sx={{
                  width: "100%",
                  height: { xs: "300px", sm: "400px", md: "500px" },
                }}>
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${selectedMedia.videoId}?autoplay=1`}
                    title={selectedMedia.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      border: "none",
                      borderRadius: "8px 8px 0 0",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Media Info */}
            <Box sx={{
              backgroundColor: "white",
              p: 3,
            }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                mb: 2,
                flexWrap: "wrap",
                gap: 1 
              }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#134e9c" }}>
                  {selectedMedia.title}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {selectedMedia.type === "image" && (
                    <IconButton
                      onClick={() => handleDownload(selectedMedia.image, selectedMedia.title)}
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "#134e9c",
                        "&:hover": {
                          backgroundColor: "#bbdefb",
                        },
                      }}
                      size="small"
                    >
                      <Download />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => handleShare(selectedMedia)}
                    sx={{
                      backgroundColor: "#e8f5e9",
                      color: "#2e7d32",
                      "&:hover": {
                        backgroundColor: "#c8e6c9",
                      },
                    }}
                    size="small"
                  >
                    <Share />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {selectedMedia.description}
              </Typography>

              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                flexWrap: "wrap",
                gap: 2,
                mb: 2 
              }}>
                <Chip
                  label={selectedMedia.type === "image" ? "PHOTO" : "VIDEO"}
                  size="small"
                  sx={{
                    backgroundColor: selectedMedia.type === "image" ? "#134e9c" : "#ff6b6b",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={selectedMedia.category}
                  size="small"
                  sx={{
                    backgroundColor: "#90ca25",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarToday sx={{ fontSize: 16, color: "#666" }} />
                  <Typography variant="body2" color="text.secondary">
                    {selectedMedia.date}
                  </Typography>
                </Box>
              </Box>

              {selectedMedia.tags && selectedMedia.tags.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {selectedMedia.tags.map((tag, index) => (
                    <Chip 
                      key={index} 
                      label={tag} 
                      size="small"
                      sx={{
                        backgroundColor: "rgba(144, 202, 37, 0.1)",
                        color: "#5a9216",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default GalleryPage;